var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { handleErrorDialog } from '@juvare/common-web-ui/DialogHelper';
import { Typography } from '@mui/material';
import { format } from 'date-fns';
import React from 'react';
export const importActionColumnTitle = 'IMPORT_ACTION';
export const delimiter = ';';
export const getValue = (value) => (value ? value.trim() : '');
export const getArrayValue = (value) => value && value.trim()
    ? value
        .split(delimiter)
        .map(getValue)
        .filter((value) => !!value)
    : [];
const getImportAction = (csvFile, row) => {
    const importActionIndex = csvFile.header.indexOf(importActionColumnTitle);
    const hasImportActionColumn = importActionIndex !== -1;
    const importActionValue = getValue(row[importActionIndex]).toLocaleLowerCase();
    const importAction = hasImportActionColumn ? (importActionValue === 'update' ? 'update' : 'create') : 'create';
    return { importAction, hasImportActionColumn };
};
export const init = (columnImportDef) => (csvFile) => __awaiter(void 0, void 0, void 0, function* () {
    var _a, _b;
    let context = {
        uniqueEmails: [],
    };
    for (let i = 0; i < columnImportDef.length; i++) {
        if (columnImportDef[i].init) {
            context = Object.assign(Object.assign({}, context), (yield ((_b = (_a = columnImportDef[i]).init) === null || _b === void 0 ? void 0 : _b.call(_a, csvFile))));
        }
    }
    return context;
});
export const validateRow = (columnImportDef, i18n, allowUpdateAccounts) => (row, csvFile, context) => __awaiter(void 0, void 0, void 0, function* () {
    var _c;
    const data = row.data.map((value) => value.trim());
    if (!data.find((value) => !!value)) {
        // skip, all columns are empty
        return row;
    }
    const importRowData = Object.assign(Object.assign({}, row), { object: {} });
    const columns = allowUpdateAccounts ? columnImportDef.length - 1 : columnImportDef.length;
    if (importRowData.data.length >= columns) {
        for (let i = 0; i < columnImportDef.length; i++) {
            const validationContext = Object.assign(Object.assign({}, context), getImportAction(csvFile, importRowData.data));
            importRowData.object = yield columnImportDef[i].validateColumn(i, importRowData, validationContext);
        }
        if (Object.keys(importRowData.object).includes('consent')) {
            importRowData.errors.push(i18n.t('contact-management:import.consentError'));
        }
        if (Object.keys(importRowData.object).includes('consentInformation')) {
            importRowData.errors.push(i18n.t('contact-management:import.consentInformationError'));
        }
        if (Object.keys(importRowData.object).includes('address')) {
            const address = importRowData.object.address;
            if (!address.state && address.countyName) {
                importRowData.warnings.push(i18n.t('contact-management:import.countyWithoutStateWarning'));
            }
            if (address.state && address.countyName && context.stateCountyMap) {
                const stateContainsCounty = (_c = context.stateCountyMap[address.state]) === null || _c === void 0 ? void 0 : _c.some((county) => county === address.countyName);
                if (!stateContainsCounty) {
                    importRowData.warnings.push(i18n.t('contact-management:import.wrongStateForCountyWarning', {
                        county: address.countyName,
                        state: address.state,
                    }));
                }
            }
        }
    }
    else {
        importRowData.errors.push(i18n.t('contact-management:import.invalidFile'));
    }
    return importRowData;
});
export const importRow = (columnImportDef, accountStorage) => (row) => __awaiter(void 0, void 0, void 0, function* () {
    if (row.object) {
        try {
            let response;
            if (row.object.importAction === 'update') {
                response = yield updateAccount(columnImportDef, row.object, accountStorage);
            }
            else {
                response = yield createAccount(columnImportDef, row.object, accountStorage);
            }
            row.object.id = response.id;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        }
        catch (error) {
            let message;
            if (error.message) {
                message = error.message;
            }
            else if (error.toString) {
                message = error.toString();
            }
            row.errors.push(message);
        }
    }
    return row;
});
export const onAfterImport = (i18n, groupStorage) => (props) => __awaiter(void 0, void 0, void 0, function* () {
    const groupName = i18n.t('contact-management:import.importGroupName', { date: format(new Date(), "MMM d yyyy HH:mm:ss 'GMT'XXX") });
    try {
        const ids = props.rows
            .filter((row) => row.object && row.object.importAction !== 'update')
            .map((row) => { var _a; return (_a = row.object) === null || _a === void 0 ? void 0 : _a.id; })
            .filter((id) => !!id);
        if (ids.length) {
            const { id: groupId } = yield groupStorage.post({
                action: 'create',
                data: {
                    name: groupName,
                    filterBased: false,
                    description: i18n.t('contact-management:import.importGroupDescription'),
                    visibility: 'private',
                    criteria: {
                        filters: [],
                    },
                },
            });
            yield groupStorage.addAccountsToGroup(groupId, ids);
            return (React.createElement(Typography, { sx: { pb: 2 } }, i18n.t('contact-management:import.groupCreatedMessage', { groupName: groupName })));
        }
        return undefined;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    }
    catch (error) {
        handleErrorDialog(error);
        return undefined;
    }
});
function createAccount(columnImportDef, rowObject, accountStorage) {
    return __awaiter(this, void 0, void 0, function* () {
        let account = {
            consent: false,
            contactEmails: [],
            contactPhoneNumbers: [],
            email: '',
            emergencyContacts: [],
            firstName: '',
            lastName: '',
            orgRole: {},
            orgStatus: {},
            systemRole: 'user',
            systemStatus: 'active',
        };
        for (let i = 0; i < columnImportDef.length; i++) {
            account = columnImportDef[i].importColumn(rowObject, account);
        }
        const response = yield accountStorage.post({ action: 'create', data: account });
        yield sleep(100); // TODO 100 ms sleep set for Okta limits testing, later need to adjust this based on findings
        return response;
    });
}
function updateAccount(columnImportDef, rowObject, accountStorage) {
    return __awaiter(this, void 0, void 0, function* () {
        const result = yield accountStorage.list({
            filter: [
                {
                    column: 'email',
                    op: '=',
                    value: rowObject.email,
                },
            ],
        });
        if (!result.total) {
            rowObject.importAction = 'create';
            return createAccount(columnImportDef, rowObject, accountStorage);
        }
        else {
            const accountId = result.rows[0].id;
            const existingAccount = yield accountStorage.get(accountId);
            let account = Object.assign({}, existingAccount.data);
            for (let i = 0; i < columnImportDef.length; i++) {
                account = columnImportDef[i].importColumn(rowObject, account);
            }
            // Prevent updating account organizations membership
            account.orgStatus = existingAccount.data.orgStatus;
            account.orgRole = existingAccount.data.orgRole;
            return accountStorage.post({ action: 'partial-update', data: account, id: accountId });
        }
    });
}
function sleep(ms) {
    return new Promise((resolve) => {
        window.setTimeout(resolve, ms);
    });
}
export const importActionColumn = (i18n) => ({
    headerName: 'IMPORT_ACTION',
    exampleValue: 'create/update',
    validateColumn: (index, row, context) => __awaiter(void 0, void 0, void 0, function* () {
        const importAction = getValue(row.data[index] || (!context.hasImportActionColumn ? context.importAction : '')).toLocaleLowerCase();
        if (!importAction) {
            row.errors.push(i18n.t('contact-management:import.importActionRequired'));
        }
        else if (!['create', 'update'].includes(importAction)) {
            row.errors.push(i18n.t('contact-management:import.invalidImportAction'));
        }
        return Object.assign(Object.assign({}, row.object), { importAction });
    }),
    importColumn: (rowObject, data) => data,
});
